import { ENVIRONMENT } from '@monetize/ui/config';
// eslint-disable-next-line no-restricted-imports
import {
  useLDClient,
  useFlags as useLDFlags,
} from 'launchdarkly-react-client-sdk';

import { ITenant, IUser } from '@monetize/types/app';
import { Maybe } from '@monetize/types/common';
import { logger } from './logger';

export type QuoteTemplates = {
  QUOTE_AFFINITY: boolean;
  QUOTE_AFFINITY_MARCH_2024: boolean;
  QUOTE_DEFAULT_1: boolean;
  QUOTE_DEFAULT_2: boolean;
  QUOTE_DEFAULT_3_EXCL_VAT: boolean;
  QUOTE_MATERIALIZE: boolean;
  QUOTE_MIDDESK: boolean;
  QUOTE_DEFAULT_1_TEST_V2: boolean;
  QUOTE_DEFAULT_2_TEST_V2: boolean;
  QUOTE_PLOTLINE: boolean;
  QUOTE_DANFOSS_EU: boolean;
  QUOTE_DANFOSS_US: boolean;
  QUOTE_LENDER: boolean;
  QUOTE_SIRAYA: boolean;
  QUOTE_SENTRY: boolean;
  QUOTE_RILLET: boolean;
  QUOTE_STARTREE: boolean;
  QUOTE_PROPER: boolean;
  QUOTE_DESCOPE: boolean;
  QUOTE_LIFTED: boolean;
  QUOTE_OPTIO: boolean;
  QUOTE_COAST: boolean;
  QUOTE_TELEPORT: boolean;
  QUOTE_PAVE: boolean;
  QUOTE_PAVE_ARR: boolean;
  QUOTE_XAGE: boolean;
  QUOTE_TRIPLEDART: boolean;
  QUOTE_ACUITYMD: boolean;
  QUOTE_CLAY: boolean;
  QUOTE_CENTERBASE_LEGALFIT: boolean;
  QUOTE_LEGALFIT: boolean;
  QUOTE_VIVIDLY: boolean;
  QUOTE_AEROSPIKE: boolean;
  QUOTE_MERCELL: boolean;
  QUOTE_ENDOR_LABS: boolean;
  QUOTE_SCHOOX: boolean;
  QUOTE_FIRST_DUE: boolean;
  QUOTE_APP_CUES: boolean;
  QUOTE_ENTR: boolean;
  QUOTE_TEMPO: boolean;
  QUOTE_FEXA: boolean;
  QUOTE_AGENT_SYNC: boolean;
  QUOTE_MONETIZENOW: boolean;
};

export type OfferingDropdownSortingType =
  | 'groupByType'
  | 'alphabetical'
  | 'groupByTypeNoHeader'
  | 'customId';

export type FeatureFlag = {
  allowEditAccountAddress: boolean;
  allowTenantDeletion: boolean;
  teleportSalesDemoTempDependentPricingConfig?: {
    derivedQuantity?: Record<
      string,
      {
        sourceProduct: string;
        quantityMultiplier: number;
      }
    >;
  };
  salesDemoMedinsightTempGuidedQuotingSpecialConfig?: {
    defaultSelectedOfferings?: Record<
      string,
      Record<string, { offeringIds: string[] }>
    >;
    quantityMapping?: { questionId: string; productIds: string[] };
    defaultQuantities?: Record<string, number>;
    lockQuantityEdit?: { productIds: string[] };
    preSelectOptionalProducts?: { productIds: string[] };
  };
  customerAutomationAcuityMd?: {
    quantityAutomation: {
      scopedOfferingIds: string[];
      sourceProductId: string;
      targetProductIds: string[];
      lockTargetQuantities: boolean;
    }[];
  };
  customerAutomationTeleport?: {
    IncludedTPRs: string;
    zeroTrustMAU: string;
    additionalZeroTrustTRPs: string;
    protectedResourcesTRPs: string;
  };
  customerAutomationLiveEo?: {
    scanCostProductId: string;
    totalScansProductId: string;
  };
  customerAutomationCoveo?: {
    customFormulaProductId: string;
  };
  customerAutomationLangChain?: {
    creditProductId: string;
    premiumSupportWithServicesProductId: string;
    premiumSupportProductId: string;
    standardSupportProductId: string;
  };
  /**
   * Fexa customer automation. The CMMS product quantity ("# of Locations")
   * drives the onboarding target price. The source and target products may live
   * in the same offering or in different offerings.
   */
  customerAutomationFexa?: {
    /** Source product whose quantity drives the onboarding price */
    cmmsProductId: string;
    /**
     * Onboarding target(s). The custom unit price is derived from the CMMS
     * quantity: unitMultiplier * (coefficient * cmmsQuantity + intercept).
     * The quantity field is locked (stays 1).
     */
    onboarding: {
      targetProductIds: string[];
      unitMultiplier: number;
      coefficient: number;
      intercept: number;
    };
  };
  /**
   * Red Oak customer automation. The "Team Communications" product's quantity
   * is locked and derived as the sum of the other (source) products' quantities
   * within the same offering.
   */
  customerAutomationRedOak?: {
    scopedOfferingIds: string[];
    /** Locked product whose quantity is derived from the source products */
    mainProductId: string;
    /** Editable products whose quantities are summed into the main product */
    sourceProductIds: string[];
  };
  /**
   * https://monetizenow.atlassian.net/browse/BP-18417
   *
   * Product custom-field driven quantity automation (roll-up + 1:1 mirror).
   * Each value is the API name of a PRODUCT custom field. Behavior lives in
   * sales-demo.utils.ts under "Custom-field quantity automation".
   */
  customerAutomationCustomFieldQuantity?: {
    /**
     * CHECKBOX. Quantity and unit price are locked; quantity is the sum of the
     * same-offering roll-up sources whose product type is Subscription.
     */
    rollUpTargetKey: string;
    /** CHECKBOX. Freely editable; feeds the roll-up target sum. */
    rollUpSourceKey: string;
    /**
     * CHECKBOX. Quantity is locked and mirrors, 1:1, the line on the quote
     * sharing its mirror match key.
     */
    quantityLockedKey: string;
    /** DROPDOWN. Pairs a quantity-locked line with the line it mirrors. */
    mirrorMatchKey: string;
  };
  /**
   * Anaplan customer automation (sales demo). Behavior lives in
   * sales-demo.utils.ts under "Anaplan Automation".
   *
   * Saving the "Apps" offering sets the QUOTE custom field `modules_enabled`
   * to the number of products on that line and applies the matching module
   * discount. The "Core SaaS Platform" custom unit price is a matrix lookup
   * keyed by the quote custom fields `of_employees` (row) and
   * `modules_enabled` (column).
   */
  customerAutomationAnaplan?: {
    /** Offering whose product count drives `modules_enabled` and the module discount */
    appsOfferingId: string;
    /** QUOTE custom field (DROPDOWN "1".."5") that receives the product count, as a string */
    modulesCustomFieldKey: string;
    /** QUOTE custom field holding the number of employees (number or numeric text) */
    employeesCustomFieldKey: string;
    /** Highest value in the `modules_enabled` dropdown; the count is clamped to it */
    maxModules: number;
    /** Offering discounts on the Apps line, one per module count; the highest `minModules` <= count wins */
    moduleDiscountTiers: { minModules: number; discountId: string }[];
    /** Offering holding the matrix-priced line */
    coreSaasOfferingId: string;
    /** CUSTOM-priced product(s) in the Core SaaS offering that receive the matrix price */
    coreSaasProductIds: string[];
    /**
     * Price matrix rows, sorted by `minEmployees` ascending; the highest row
     * <= employees wins. Column = min(modules, unitPriceByModules.length) - 1,
     * so the last column covers "3 or more" modules.
     */
    employeeTiers: { minEmployees: number; unitPriceByModules: number[] }[];
  };
  disableDiscountLifted?: {
    productCustomIds: string[];
  };
  createProductionTenant: boolean;
  metabaseReporting: boolean;
  revenueRecognition: boolean;
  quoteOfferingDateAndScheduledChangeRate: boolean;
  totalExclVat: boolean;
  quoteTemplates: QuoteTemplates;
  pdfTemplateEditor?: boolean;
  showQuotableField: boolean;
  /**
   * -1 indicates no limit
   * any other positive number indicates the limit where electronic payment is allowed
   */
  anonymousInvoiceAllowPaymentAmount: -1 | number;
  allowMinCommitAdditionalFrequencies: boolean;
  offeringDropdownSorting: OfferingDropdownSortingType;
  dunningEmailTemplates: { label: string; value: string }[];
  allowBillGroupStatusAutomation: boolean;
  useDealRoom: boolean;
  dealRoomEnableAccept: boolean;
  allowNetsuiteFieldMapping: boolean;
  showSubscriptionOnAccount: boolean;
  allowCreditAndCreditNoteVoid: boolean;
  enableBillingSettings: boolean;
  showPastDueFields: boolean;
  integrationLogSettings: boolean;
  enableQuantityDropDown?: {
    productIds?: string[];
  };
  slackSettings: boolean;
  useTaxwireConfig: boolean;
  allowQuantityDropdown: boolean;
  enableUpliftCalculator: boolean;
  /** Shows the quote-level "Renewal Uplift" row + drawer in the quote setup panel */
  enableQuoteLevelUplift: boolean;
  allowSelfServeRate: boolean;
  showInvoiceAccountingDate: boolean;
  enableLegalEntityInvoiceTerms: boolean;
  useAiChat: boolean;
  showProductPpuOption: boolean;
  enableGatewayAccountModal: boolean;
  enableGatewayAccountUnlink: boolean;
  tempDescopeQuoteSharePayment?:
    | { enabled: false; quoteNameContains?: null }
    | { enabled: true; quoteNameContains: string };
  showRilletConnector: boolean;
  enableAddWireTransferPaymentMethod: boolean;
  enableGatewayAccountLink: boolean;
  enableTokenCredits: boolean;
  enableRevRecV2: boolean;
  enableBlockPricing: boolean;
  enableQuoteOutputLanguageSettings: boolean;
  enableDiscountApplicationMethodSettings: boolean;
  useVertexConfig: boolean;
  enableMultiSelectCustomField: boolean;
  enableCustomFieldVisibility: boolean;
  enableOnetimeDiscountProduct: boolean;
  enableQuoteDocumentManagement: boolean;
  enableAccountMerge: boolean;
  customerPortalEnabled: boolean;
  accountHierarchy: boolean;
  enableQuoteShareRedirect: boolean;
  enableTenantQuoteDocumentManagement: boolean;
  enableInvoiceSplit: boolean;
  enableUsageCustomFields: boolean;
  enableUpdatePaymentInstructions: boolean;
  enableRankOrderedApprovalGating: boolean;
  enableAutoPaymentTiming: boolean;
  enableDocxSupport: boolean;
  enableDocxQuoteDownload: boolean;
  enableInvoiceVoid: boolean;
  enableQuoteYearlyBreakdown: boolean;
  enableProductCatalogSyncToHubspot: boolean;
  /** Shows the "Enable Upfront Billing" toggle in Quote Settings > Additional Settings */
  showUpfrontBillingSetting: boolean;
};

export const ENABLE_LAUNCHDARKLY = !ENVIRONMENT.IS_LOCAL_DEV;

/**
 * Initialize LaunchDarkly context
 * For dev, localhost, and preview environments we use one shared context
 *
 * @param ldClient
 * @param currentTenantUser
 * @param currentTenant
 */
export const initLaunchDarklyContext = async (
  ldClient: ReturnType<typeof useLDClient>,
  currentTenantUser: Maybe<IUser>,
  currentTenant: Maybe<ITenant>,
) => {
  try {
    if (
      !ENABLE_LAUNCHDARKLY ||
      !currentTenantUser ||
      !ldClient ||
      !currentTenant
    ) {
      return;
    }
    // Shared context for CI / Playwright to limit usage
    if (ENVIRONMENT.IS_PLAYWRIGHT) {
      await ldClient.identify({
        kind: 'user',
        key: 'SHARED_DEV_USER',
        anonymous: false,
        name: 'SHARED_DEV_USER',
        email: 'everyone@monetizenow.io',
      });
    } else if (ENVIRONMENT.IS_DEV) {
      // Dev we limit usage for user but allow targeting tenants
      await ldClient.identify({
        kind: 'tenant',
        key: currentTenant.id,
        anonymous: false,
        name: `${currentTenant.id} ${currentTenant.name} [${
          currentTenant.testTenant ? 'TEST' : 'LIVE'
        }]`,
        tenantId: currentTenant.id,
        tenantName: currentTenant.name,
        testTenant: currentTenant.testTenant,
        host: ENVIRONMENT.HOST_TYPE,
      });
    } else {
      // In staging/production we allow targeting users and tenants
      await ldClient.identify({
        kind: 'multi',
        user: {
          key: currentTenantUser.id,
          anonymous: false,
          name: currentTenantUser.name,
          email: currentTenantUser.email,
        },
        tenant: {
          key: currentTenant.id,
          anonymous: false,
          name: `${currentTenant.id} ${currentTenant.name} [${
            currentTenant.testTenant ? 'TEST' : 'LIVE'
          }]`,
          tenantId: currentTenant.id,
          tenantName: currentTenant.name,
          testTenant: currentTenant.testTenant,
          host: ENVIRONMENT.HOST_TYPE,
        },
      });
    }
    logger.info('Initialized LaunchDarkly context', ldClient.getContext());
  } catch (ex) {
    logger.warn('Error initializing LaunchDarkly context', ex);
  }
};

/**
 * Used for invoice share links
 * We know the tenantId but not the user
 */
export const initLaunchDarklyContextAnonymous = (
  ldClient: ReturnType<typeof useLDClient>,
  tenantId: string,
  shareId?: Maybe<string>,
) => {
  try {
    if (ENABLE_LAUNCHDARKLY && ldClient && tenantId) {
      ldClient.identify({
        kind: 'multi',
        user: { kind: 'user', key: 'invoice_share', name: 'invoice_share' },
        tenant: {
          key: tenantId,
          anonymous: false,
          tenantId,
        },
      });
    }
  } catch (ex) {
    logger.warn('Error initializing LaunchDarkly context', ex);
  }
};

/** Used for localhost environments */
const DEFAULT_FEATURE_FLAGS: FeatureFlag = {
  allowEditAccountAddress: true,
  allowTenantDeletion: true,
  teleportSalesDemoTempDependentPricingConfig: {
    derivedQuantity: {
      prod_5iURYkr2SVpvS: {
        quantityMultiplier: 10,
        sourceProduct: 'prod_f9Y1aNWQtRq7i',
      },
      prod_D5IqfwNLElAvu: {
        quantityMultiplier: 1000,
        sourceProduct: 'prod_f9Y1aNWQtRq7i',
      },
    },
  },
  salesDemoMedinsightTempGuidedQuotingSpecialConfig: {
    defaultSelectedOfferings: {
      ques_uUvLlnkAynax7: {
        Core: {
          offeringIds: ['offr_6cnY4GaRxH8t4', 'offr_gBWuQx4hw4FvE'],
        },
        Standalone: {
          offeringIds: ['offr_qdYAMc9JhZ3aR'],
        },
      },
      ques_Z8gkYbECUZ1fo: {
        Y: {
          offeringIds: ['offr_mIC6V8ifmUs03'],
        },
      },
    },
    defaultQuantities: {
      prod_3twFfpiKpQqqO: 1,
    },
    lockQuantityEdit: {
      productIds: [
        'prod_xuf8uPhrBdcvE',
        'prod_tL0D4CZ0QUBfG',
        'prod_ZgEv8CGFIo6EU',
        'prod_zKZLyGa4WngDv',
        'prod_tKClThJcDbsgj',
        'prod_o5SVgxHVYc7xH',
        'prod_hNFRPktsigZiV',
        'prod_QZ7TKi3AmHHNf',
        'prod_rqOGTfuWVfIzh',
        'prod_5CPXp2OoeIwFo',
        'prod_BdUDEPZJsEhEN',
      ],
    },
    preSelectOptionalProducts: {
      productIds: [
        'prod_ZgEv8CGFIo6EU',
        'prod_zKZLyGa4WngDv',
        'prod_hNFRPktsigZiV',
        'prod_3twFfpiKpQqqO',
        'prod_5CPXp2OoeIwFo',
      ],
    },
    quantityMapping: {
      productIds: [
        'prod_xuf8uPhrBdcvE',
        'prod_tL0D4CZ0QUBfG',
        'prod_ZgEv8CGFIo6EU',
        'prod_zKZLyGa4WngDv',
        'prod_tKClThJcDbsgj',
        'prod_o5SVgxHVYc7xH',
        'prod_hNFRPktsigZiV',
        'prod_QZ7TKi3AmHHNf',
        'prod_rqOGTfuWVfIzh',
        'prod_5CPXp2OoeIwFo',
        'prod_BdUDEPZJsEhEN',
      ],
      questionId: 'ques_zY0yksASM3civ',
    },
  },
  customerAutomationAcuityMd: {
    quantityAutomation: [
      {
        scopedOfferingIds: ['offr_6cnY4GaRxH8t4', 'offr_gBWuQx4hw4FvE'],
        sourceProductId: 'prod_4bwWa6ocNHCCY',
        targetProductIds: [
          'prod_Rkb48xN8myMUC',
          'prod_pPaZFjmnHzE4L',
          'prod_AVjkRK4qKkwaN',
          'prod_iJppfOHNSYIWV',
          'prod_cst1yKIUHYTkK',
          'prod_0xvZAC5UQjKQY',
          'prod_4pZvEvCzjVHYN',
          'prod_qnWayyErptxTM',
          'prod_A8vjJ5rd2lsOE',
          'prod_cHuu51aafEDmT',
          'prod_Hj7gKU4rKw0Id',
          'prod_K66unY03GdtvJ',
          'prod_GhKV1EhsgmWJI',
          'prod_72wIx3AMu3qHx',
          'prod_KmB28ECpVArCd',
          'prod_fJSl44XEKhBuA',
          'prod_P7nioFisdpCps',
          'prod_4OB0vObRMpfSH',
        ],
        lockTargetQuantities: true,
      },
      // Playwright Test Tenant 1 - tennt_u6HJNDIXL6CkW2ev
      {
        scopedOfferingIds: ['offr_nXK9ByNGI0NXC', 'offr_7ecYF1BR5o7Zr'],
        sourceProductId: 'prod_sgdUcD1LW5oSe',
        targetProductIds: ['prod_xqhWkb3YSqtcf', 'prod_mXdbw6VFZMChF'],
        lockTargetQuantities: true,
      },
    ],
  },
  customerAutomationTeleport: {
    IncludedTPRs: 'prod_wZ1JDUXhCkWd0',
    zeroTrustMAU: 'prod_TNZiXKRQ76bh0',
    additionalZeroTrustTRPs: 'prod_GhDTwh2oYfJ8P',
    protectedResourcesTRPs: 'prod_UfdVaeRxvwL2Y',
  },
  customerAutomationLiveEo: {
    scanCostProductId: 'prod_yeEp5dAUAMgvg',
    totalScansProductId: 'prod_wx5ICnJ3Mx0C5',
  },
  customerAutomationCoveo: {
    customFormulaProductId: 'prod_uO60G7ud8C9pG',
  },
  customerAutomationLangChain: {
    creditProductId: 'prod_6Yopucq6YR033',
    premiumSupportWithServicesProductId: 'prod_HMSqUdI7fzcha',
    premiumSupportProductId: 'prod_9lbEawQSmb6xI',
    standardSupportProductId: 'prod_Keo8vpM8ijPI5',
  },
  customerAutomationFexa: {
    cmmsProductId: 'prod_DVIOkhnoa1ENi',
    onboarding: {
      targetProductIds: ['prod_krI5leMP5ymQi'],
      unitMultiplier: 200,
      coefficient: 0.531,
      intercept: 113.9,
    },
  },
  customerAutomationRedOak: {
    scopedOfferingIds: ['offr_ni64DoIvdXdVC'],
    mainProductId: 'prod_AYXSyYPGDYhNI',
    sourceProductIds: [
      'prod_dkTfe73fxL9R5',
      'prod_fomGLiW2E2iBc',
      'prod_YZrOQ4Xxhwue5',
    ],
  },
  customerAutomationCustomFieldQuantity: {
    rollUpTargetKey: 'is_roll_up_target',
    rollUpSourceKey: 'is_roll_up_source',
    quantityLockedKey: 'is_quantity_locked',
    mirrorMatchKey: 'mirror_match_key',
  },
  customerAutomationAnaplan: {
    appsOfferingId: 'offr_4Fpy5xl8oQxr2',
    modulesCustomFieldKey: 'modules_enabled',
    employeesCustomFieldKey: 'of_employees',
    maxModules: 5,
    moduleDiscountTiers: [
      { minModules: 1, discountId: 'disc_pTcfBJycSMJxt' },
      { minModules: 2, discountId: 'disc_oBevwhSQcdPpL' },
      { minModules: 3, discountId: 'disc_o01cMEEXP5bZ2' },
    ],
    coreSaasOfferingId: 'offr_72VUeZfJDt5kT',
    coreSaasProductIds: ['prod_TH3VRfZavNmts'],
    employeeTiers: [
      { minEmployees: 0, unitPriceByModules: [30_000, 40_000, 50_000] },
      { minEmployees: 100, unitPriceByModules: [55_000, 65_000, 75_000] },
      { minEmployees: 500, unitPriceByModules: [80_000, 90_000, 100_000] },
      { minEmployees: 1_000, unitPriceByModules: [105_000, 115_000, 125_000] },
    ],
  },
  disableDiscountLifted: {
    productCustomIds: [
      'EOR_Sourced_Spend',
      'AOR_Sourced_Spend',
      'IC_Sourced_Spend',
      'StaffAug_Spend',
      'Outsourcing_Spend',
      'EOR_Pre-ID_Spend',
      'AOR_Pre-ID_Spend',
      'IC_Pre-ID_Spend',
    ],
  },
  createProductionTenant: false,
  metabaseReporting: true,
  revenueRecognition: true,
  quoteOfferingDateAndScheduledChangeRate: false,
  totalExclVat: false,
  quoteTemplates: {
    QUOTE_AFFINITY: true,
    QUOTE_AFFINITY_MARCH_2024: true,
    QUOTE_DEFAULT_1: true,
    QUOTE_DEFAULT_2: true,
    QUOTE_DEFAULT_3_EXCL_VAT: true,
    QUOTE_MATERIALIZE: true,
    QUOTE_MIDDESK: true,
    QUOTE_DEFAULT_1_TEST_V2: true,
    QUOTE_DEFAULT_2_TEST_V2: true,
    QUOTE_PLOTLINE: true,
    QUOTE_DANFOSS_EU: true,
    QUOTE_DANFOSS_US: true,
    QUOTE_LENDER: true,
    QUOTE_SIRAYA: true,
    QUOTE_SENTRY: true,
    QUOTE_RILLET: true,
    QUOTE_STARTREE: true,
    QUOTE_PROPER: true,
    QUOTE_DESCOPE: true,
    QUOTE_LIFTED: true,
    QUOTE_OPTIO: true,
    QUOTE_COAST: true,
    QUOTE_TELEPORT: true,
    QUOTE_PAVE: true,
    QUOTE_PAVE_ARR: true,
    QUOTE_TRIPLEDART: true,
    QUOTE_XAGE: true,
    QUOTE_ACUITYMD: true,
    QUOTE_CLAY: true,
    QUOTE_CENTERBASE_LEGALFIT: true,
    QUOTE_LEGALFIT: true,
    QUOTE_VIVIDLY: true,
    QUOTE_AEROSPIKE: true,
    QUOTE_MERCELL: true,
    QUOTE_ENDOR_LABS: true,
    QUOTE_SCHOOX: true,
    QUOTE_FIRST_DUE: true,
    QUOTE_APP_CUES: true,
    QUOTE_ENTR: true,
    QUOTE_TEMPO: true,
    QUOTE_FEXA: true,
    QUOTE_AGENT_SYNC: true,
    QUOTE_MONETIZENOW: true,
  },
  pdfTemplateEditor: true,
  showQuotableField: true,
  anonymousInvoiceAllowPaymentAmount: -1,
  allowMinCommitAdditionalFrequencies: true,
  offeringDropdownSorting: 'groupByType',
  // TODO: replace with universal list instead of customer specific
  dunningEmailTemplates: [
    {
      label: 'Affinity Reminder Template',
      value: 'd-e7a983bd771343ada7429b7c6a695e8c',
    },
    {
      label: 'Affinity 0-21 Day Template',
      value: 'd-8172169478fc46df871468bacabe2284',
    },
    {
      label: 'Affinity 30-45 Day Template',
      value: 'd-f71639b166174aefb8f7143f845ce8a6',
    },
    {
      label: 'Affinity 45 Days Past Due Template',
      value: 'd-0ae393bc247a44a29528d33bcd661688',
    },
  ],
  allowBillGroupStatusAutomation: false,
  useDealRoom: true,
  dealRoomEnableAccept: true,
  allowNetsuiteFieldMapping: true,
  showSubscriptionOnAccount: true,
  allowCreditAndCreditNoteVoid: true,
  enableBillingSettings: true,
  showPastDueFields: false,
  integrationLogSettings: true,
  enableQuantityDropDown: {
    productIds: ['prod_RfPJEuRzyrpAv'],
  },
  slackSettings: true,
  useTaxwireConfig: true,
  allowQuantityDropdown: true,
  enableUpliftCalculator: true,
  enableQuoteLevelUplift: true,
  allowSelfServeRate: true,
  showInvoiceAccountingDate: true,
  enableLegalEntityInvoiceTerms: true,
  useAiChat: true,
  showProductPpuOption: true,
  enableGatewayAccountModal: true,
  enableGatewayAccountUnlink: true,
  tempDescopeQuoteSharePayment: {
    enabled: true,
    quoteNameContains: 'Start-up Program',
  },
  showRilletConnector: true,
  enableAddWireTransferPaymentMethod: true,
  enableGatewayAccountLink: true,
  enableTokenCredits: true,
  enableRevRecV2: true,
  enableBlockPricing: true,
  enableQuoteOutputLanguageSettings: true,
  useVertexConfig: true,
  enableMultiSelectCustomField: true,
  enableCustomFieldVisibility: true,
  enableOnetimeDiscountProduct: true,
  enableQuoteDocumentManagement: true,
  accountHierarchy: true,
  customerPortalEnabled: true,
  enableAccountMerge: true,
  enableQuoteShareRedirect: true,
  enableTenantQuoteDocumentManagement: true,
  enableUpdatePaymentInstructions: true,
  enableInvoiceSplit: true,
  enableUsageCustomFields: true,
  enableDiscountApplicationMethodSettings: true,
  enableRankOrderedApprovalGating: true,
  enableAutoPaymentTiming: true,
  enableDocxSupport: true,
  enableDocxQuoteDownload: true,
  enableInvoiceVoid: true,
  enableQuoteYearlyBreakdown: true,
  enableProductCatalogSyncToHubspot: true,
  showUpfrontBillingSetting: false,
};
/**
 * PR preview deployments (`preview-<branch>.monetizeplatform.com`) are built
 * with the CI flag set but should use real LaunchDarkly flags like dev does.
 */
export const isPreviewHost = (hostname: string) =>
  hostname.startsWith('preview-');

/**
 * Use this hook in any components that require feature flags
 */
export const useFlags = (): FeatureFlag => {
  const featureFlags: FeatureFlag = useLDFlags();

  if (
    (ENVIRONMENT.IS_CI && !isPreviewHost(location.hostname)) ||
    ENVIRONMENT.IS_LOCAL_DEV
  ) {
    return (window.__PLAYWRIGHT_LAUNCHDARKLY_FLAGS__ ||
      DEFAULT_FEATURE_FLAGS) as FeatureFlag;
  }

  if (ENABLE_LAUNCHDARKLY) {
    return featureFlags;
  }

  return DEFAULT_FEATURE_FLAGS;
};

export const LDProviderConfig = {
  clientSideID: ENVIRONMENT.LAUNCH_DARKLY_CLIENT_ID,
  context: { kind: 'user', key: 'anonymous', anonymous: true },
  reactOptions: {
    useCamelCaseFlagKeys: true,
  },
  /** Seconds to wait for the first flag fetch before rendering anyway */
  timeout: 5,
  options: {
    /**
     * Serve the last fetched flags from localStorage right away and refresh
     * them in the background, so a repeat visit does not wait on LaunchDarkly
     * before the app can mount. identify() still fetches fresh flags.
     */
    bootstrap: 'localStorage' as const,
  },
};
